import {
    AddinsFlexPaneBody as LazyAddinsFlexPaneBody,
    AddinsFlexPaneHeader as LazyAddinsFlexPaneHeader,
} from 'owa-addins-view';
import { getAddinMailboxInfoForWindow } from 'owa-addins-view/lib/utils/getAddinMailboxInfoForWindow';
import getDrawerPaneId from 'owa-app-pane-group-utils/lib/getDrawerPaneId';
import { createLazyComponent, LazyModule, type LazyComponent } from 'owa-bundling';
import { isCapabilityEnabled } from 'owa-capabilities';
import { addinsResizablePaneCapability } from 'owa-capabilities-definitions/lib/addinsResizablePaneCapability';
import { addinsUseDrawerPaneInCalendarCapability } from 'owa-capabilities-definitions/lib/addinsUseDrawerPaneInCalendarCapability';
import { addinsUseDrawerPaneInpopoutsCapability } from 'owa-capabilities-definitions/lib/addinsUseDrawerPaneInpopoutsCapability';
import type { MailboxInfo } from 'owa-client-types';
import { getApp } from 'owa-config';
import { isInlineFormEnabled } from 'owa-copilot-settings-utils/lib/utils/isCalendarEventFormV2Enabled';
import { DrawerPane } from 'owa-drawer';
import { observer } from 'owa-mobx-react';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { useContext, type FunctionComponent } from 'react';
import { addinsHeader, addinsBody, addinsTitle } from '../AppPaneGroup.scss';
import type * as AddinsResizableDrawerModule from './AddinsResizableDrawer';

const lazyResizableDrawerModule: LazyModule<typeof AddinsResizableDrawerModule> = new LazyModule(
    () =>
        import(
            /* webpackChunkName: "AddinsResizableDrawer" */
            './AddinsResizableDrawer'
        ),
    { name: 'AddinsResizableDrawer' }
);

const LazyAddinsResizableDrawer: LazyComponent<
    AddinsResizableDrawerModule.AddinsResizableDrawerProps,
    unknown,
    void
> = createLazyComponent(lazyResizableDrawerModule, m => m.AddinsResizableDrawer);

function createAddinsHeader(mailboxInfo: MailboxInfo) {
    return <LazyAddinsFlexPaneHeader mailboxInfo={mailboxInfo} renderTaskPaneInPanel={true} />;
}

const defaultExport: FunctionComponent<{}> = observer(function AddinsPane() {
    const paneId = getDrawerPaneId('addins');
    const targetWindow = useContext(ProjectionContext) ?? window;
    const isPopoutWindow = isPopout(targetWindow);
    const isMail = getApp() === 'Mail';
    // Match the calendar-specific predicate enforced by TaskPaneCollection and
    // openNonPersistentTaskPaneAddinCommand so that mail windows don't switch
    // away from getModuleContextMailboxInfo() once the capability rolls out.
    const useCalendarDrawerMailbox =
        isCapabilityEnabled(addinsUseDrawerPaneInCalendarCapability) &&
        !isMail &&
        (isPopoutWindow || isInlineFormEnabled());
    const mailboxInfo =
        (isCapabilityEnabled(addinsUseDrawerPaneInpopoutsCapability) && isPopoutWindow) ||
        useCalendarDrawerMailbox
            ? getAddinMailboxInfoForWindow(targetWindow) ?? getModuleContextMailboxInfo()
            : getModuleContextMailboxInfo();

    if (isCapabilityEnabled(addinsResizablePaneCapability)) {
        return (
            <LazyAddinsResizableDrawer
                paneId={paneId}
                mailboxInfo={mailboxInfo}
                targetWindow={targetWindow}
                isPopoutWindow={isPopoutWindow}
            />
        );
    }

    const headerText = createAddinsHeader(mailboxInfo);

    return (
        <DrawerPane
            paneId={paneId}
            headerText={headerText}
            headerClassName={addinsHeader}
            titleClassName={addinsTitle}
            bodyClassName={addinsBody}
            showCloseButton={false}
            preserveContentOnDismiss={true}
            role={'complementary'}
        >
            <LazyAddinsFlexPaneBody mailboxInfo={mailboxInfo} />
        </DrawerPane>
    );
}, 'AddinsPane');
export default defaultExport;
